import { useEffect, useState } from 'react'
import { ChevronDown, ChevronUp, ChevronRight, SlidersHorizontal, Check, Plus, X, Zap, Info } from 'lucide-react'
import { labelCls, inputCls } from '../styles'
import {
  IDV_FLOWS, IDV_PROVIDERS, DOC_TYPES, IDV_COUNTRIES, DEFAULT_COUNTRY_RESTRICTIONS,
  DEFAULT_VENDOR_SETTINGS, DEFAULT_SMS_CONFIG, DEFAULT_AUDIT_EVENTS,
} from '../idvConfigData'
import type { CountryDocRestriction, IdvProvider, IdvVendorSettings, IdvVendorProcessConfig } from '../types'
import type { IdvDiagramParams } from '../dynamicInteractionDiagrams'
import { BuilderLockedBanner } from './BuilderLockedBanner'
import { useAiSeed } from '../aiConfigure'
import type { AiSeed } from '../aiConfigure'

export type IdvDiagramConfig = Omit<IdvDiagramParams, 'platformVersion' | 'flow'>

export interface IdvAiSuggestion {
  defaultProvider: IdvProvider
  autoRunKyc: boolean
}

interface IdvConfigPanelProps {
  activeFlowId: string
  onActiveFlowChange: (flowId: string) => void
  onDiagramConfigChange: (config: IdvDiagramConfig) => void
  /** True once an IDV step already exists in the workflow picked in Workflow
   * Builder — real workflow JSON has no vendor/flow field to derive a specific
   * choice from, so this just freezes whatever flow is currently active rather
   * than re-asking, instead of deriving a new selection. */
  lockedByBuilder?: boolean
  /** AI Configure from Scope — applied once the rep accepts the suggestion. */
  aiSeed?: AiSeed<IdvAiSuggestion> | null
}

function Toggle({ checked, onChange, disabled }: { checked: boolean; onChange: () => void; disabled?: boolean }) {
  return (
    <label className="relative inline-flex items-center shrink-0" style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}>
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} className="sr-only peer" />
      <div className="w-8 h-[18px] rounded-full transition-colors" style={{ background: checked ? '#6366f1' : '#e5e7eb' }} />
      <div className="absolute left-[3px] top-[3px] w-3 h-3 rounded-full bg-white transition-transform" style={{ transform: checked ? 'translateX(14px)' : 'translateX(0)' }} />
    </label>
  )
}

function SubSection({ title, description, children, defaultOpen = false }: { title: string; description: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="rounded-[10px] overflow-hidden" style={{ border: '1px solid #e5e7eb' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-[#ffffff]"
        style={{ background: '#f8fafc' }}
      >
        {open ? <ChevronDown size={12} className="text-[#64748b] shrink-0" /> : <ChevronRight size={12} className="text-[#64748b] shrink-0" />}
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold text-[#1e293b]">{title}</p>
          <p className="text-[10px] text-[#64748b] mt-0.5 leading-snug">{description}</p>
        </div>
      </button>
      {open && <div className="p-3 space-y-3" style={{ borderTop: '1px solid #e5e7eb' }}>{children}</div>}
    </div>
  )
}

function ProcessConfigFields({ label, value, onChange, disabled }: { label: string; value: IdvVendorProcessConfig; onChange: (next: IdvVendorProcessConfig) => void; disabled?: boolean }) {
  return (
    <div className="rounded-[9px] p-3" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
      <p className="text-[10px] font-semibold text-[#475569] mb-2">{label}</p>
      <div className="grid grid-cols-[1fr_1fr_70px] gap-2">
        <div>
          <label className={labelCls}>Process Def Name</label>
          <input className={inputCls} value={value.processDefName} onChange={(e) => onChange({ ...value, processDefName: e.target.value })} placeholder="e.g. DevOnboardingFlow" disabled={disabled} />
        </div>
        <div>
          <label className={labelCls}>Process Def ID</label>
          <input className={inputCls} value={value.processDefId} onChange={(e) => onChange({ ...value, processDefId: e.target.value })} placeholder="e.g. NL3YSVB6FWAC7Z5KBMJJZR3T3M" disabled={disabled} />
        </div>
        <div>
          <label className={labelCls}>Version</label>
          <input type="number" min={1} className={inputCls} value={value.version} onChange={(e) => onChange({ ...value, version: Number(e.target.value) || 1 })} disabled={disabled} />
        </div>
      </div>
    </div>
  )
}

export function IdvConfigPanel({ activeFlowId, onActiveFlowChange, onDiagramConfigChange, lockedByBuilder = false, aiSeed }: IdvConfigPanelProps) {
  const [open, setOpen] = useState(true)
  const [defaultProvider, setDefaultProvider] = useState<IdvProvider>('Daon')
  const isCustomFlow = !IDV_FLOWS.some((f) => f.flowId === activeFlowId)
  const [customFlowId, setCustomFlowId] = useState(isCustomFlow ? activeFlowId : '')
  function applyCustomFlow() {
    const v = customFlowId.trim()
    if (v) onActiveFlowChange(v)
  }
  const [restrictions, setRestrictions] = useState<CountryDocRestriction[]>(DEFAULT_COUNTRY_RESTRICTIONS)
  const [addCountryOpen, setAddCountryOpen] = useState(false)

  const [autoRunKyc, setAutoRunKyc] = useState(true)
  const [useHostedOneSdk, setUseHostedOneSdk] = useState(true)
  const [cleanupUrlsOnComplete, setCleanupUrlsOnComplete] = useState(true)

  const [smsConfig, setSmsConfig] = useState(DEFAULT_SMS_CONFIG)

  const [vendorSettings, setVendorSettings] = useState<Record<IdvProvider, IdvVendorSettings>>(DEFAULT_VENDOR_SETTINGS)

  const [forwardAuditEvents, setForwardAuditEvents] = useState(false)
  const [auditEvents, setAuditEvents] = useState<string[]>(DEFAULT_AUDIT_EVENTS)
  const [newAuditEvent, setNewAuditEvent] = useState('')

  useAiSeed(aiSeed, (suggestion) => {
    setDefaultProvider(suggestion.defaultProvider)
    setAutoRunKyc(suggestion.autoRunKyc)
  })

  // Keep the parent stage's live-config-driven diagram in sync with every setting that materially changes it.
  useEffect(() => {
    onDiagramConfigChange({ defaultProvider, vendorSettings, smsConfig, autoRunKyc, cleanupUrlsOnComplete })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultProvider, vendorSettings, smsConfig, autoRunKyc, cleanupUrlsOnComplete])

  const activeFlow = IDV_FLOWS.find((f) => f.flowId === activeFlowId)
  const availableCountries = IDV_COUNTRIES.filter((c) => !restrictions.some((r) => r.countryCode === c.code))

  function toggleDocType(countryCode: string, docType: string) {
    setRestrictions((prev) => prev.map((r) => {
      if (r.countryCode !== countryCode) return r
      const has = r.docTypes.includes(docType)
      return { ...r, docTypes: has ? r.docTypes.filter((d) => d !== docType) : [...r.docTypes, docType] }
    }))
  }

  function toggleRestrict(countryCode: string) {
    setRestrictions((prev) => prev.map((r) => (r.countryCode === countryCode ? { ...r, restrict: !r.restrict } : r)))
  }

  function removeCountry(countryCode: string) {
    setRestrictions((prev) => prev.filter((r) => r.countryCode !== countryCode))
  }

  function addCountry(code: string, label: string) {
    setRestrictions((prev) => [...prev, { countryCode: code, countryLabel: label, restrict: true, docTypes: [] }])
    setAddCountryOpen(false)
  }

  function updateVendor(provider: IdvProvider, next: Partial<IdvVendorSettings>) {
    setVendorSettings((prev) => ({ ...prev, [provider]: { ...prev[provider], ...next } }))
  }

  function addAuditEvent() {
    const v = newAuditEvent.trim()
    if (!v || auditEvents.includes(v)) return
    setAuditEvents((prev) => [...prev, v])
    setNewAuditEvent('')
  }

  function removeAuditEvent(pattern: string) {
    setAuditEvents((prev) => prev.filter((p) => p !== pattern))
  }

  return (
    <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 transition-colors hover:bg-[#ffffff]"
        style={{ background: '#f8fafc' }}
      >
        <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">
          <SlidersHorizontal size={12} className="text-[#4f46e5]" />
          IDV Configuration
        </span>
        {open ? <ChevronUp size={12} className="text-[#64748b]" /> : <ChevronDown size={12} className="text-[#64748b]" />}
      </button>

      {open && (
        <div className="p-4 space-y-3">
          <p className="text-[10px] text-[#475569]">
            Modelled on Operations Center&apos;s IDV &gt; Configuration tab. The flow marked <span className="text-emerald-700 font-semibold">active</span> below is what &quot;Start IDV Capture&quot; actually sends to FrankieOne — everything else here mirrors the real tab for completeness, but isn&apos;t independently wired to a live call.
          </p>

          <SubSection title="Verification / Workflow Trigger" description="Controls when KYC verification (V1) / workflow (V2) is triggered for this customer" defaultOpen>
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-[9px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-[#1e293b]">Automatically run KYC verification when IDV completes</p>
                <p className="text-[10px] text-[#64748b] mt-0.5">When off, IDV capture and KYC verification are two separate, manually-triggered steps for this customer.</p>
              </div>
              <Toggle checked={autoRunKyc} onChange={() => setAutoRunKyc((v) => !v)} />
            </div>
          </SubSection>

          <SubSection title="Hosted OneSDK" description="Verification links are generated by the IDV service; SMS delivery is handled by the backend" defaultOpen>
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-[9px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-[#1e293b]">Use Hosted OneSDK</p>
                <p className="text-[10px] text-[#64748b] mt-0.5">This demo always uses Hosted OneSDK delivery — &quot;Start IDV Capture&quot; wouldn&apos;t have anywhere else to send the applicant.</p>
              </div>
              <Toggle checked={useHostedOneSdk} onChange={() => setUseHostedOneSdk((v) => !v)} />
            </div>
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-[9px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-[#1e293b]">Clean up verification links once complete</p>
                <p className="text-[10px] text-[#64748b] mt-0.5">Expires the hosted URL immediately after the applicant finishes, so it can&apos;t be reused.</p>
              </div>
              <Toggle checked={cleanupUrlsOnComplete} onChange={() => setCleanupUrlsOnComplete((v) => !v)} />
            </div>
          </SubSection>

          {/* Flows table */}
          <div>
            <label className={labelCls}>IDV Flows · {IDV_FLOWS.length}</label>
            {lockedByBuilder && (
              <div className="mb-2">
                <BuilderLockedBanner>
                  The workflow already configured in Workflow Builder includes an IDV step — keeping <span className="font-semibold">{activeFlow?.name ?? activeFlowId}</span> as the confirmed flow for this journey. Change the flow in Workflow Builder if it needs to be different.
                </BuilderLockedBanner>
              </div>
            )}
            <div className="rounded-[10px] overflow-hidden" style={{ border: '1px solid #e5e7eb' }}>
              <div className="grid grid-cols-[1fr_140px_140px_90px] gap-2 px-3 py-2 text-[9px] font-semibold uppercase tracking-wider text-[#475569]" style={{ background: '#f8fafc' }}>
                <span>Flow Name</span>
                <span>Type</span>
                <span>Flow ID</span>
                <span className="text-right">Active</span>
              </div>
              <div className="divide-y" style={{ borderColor: '#e5e7eb' }}>
                {IDV_FLOWS.map((flow) => {
                  const isActive = flow.flowId === activeFlowId
                  return (
                    <div
                      key={flow.id}
                      className="grid grid-cols-[1fr_140px_140px_90px] gap-2 px-3 py-2 items-center"
                      style={{
                        background: isActive ? 'rgba(99,102,241,.08)' : undefined,
                        opacity: lockedByBuilder && !isActive ? 0.5 : 1,
                      }}
                    >
                      <span className="text-[11px] text-[#1e293b] flex items-center gap-1.5 truncate">
                        {flow.name}
                        {flow.provider && (
                          <span className="text-[9px] font-medium px-1.5 py-0.5 rounded" style={{ background: 'rgba(52,211,153,.12)', color: '#15803d' }}>
                            {flow.provider}
                          </span>
                        )}
                      </span>
                      <span className="text-[10px] text-[#64748b] truncate">{flow.type}</span>
                      <span className="text-[10px] font-mono text-[#64748b] truncate">{flow.flowId}</span>
                      <div className="flex justify-end">
                        <button
                          onClick={() => onActiveFlowChange(flow.flowId)}
                          disabled={lockedByBuilder}
                          className="flex items-center gap-1 px-2 py-1 rounded-[6px] text-[9px] font-semibold transition-colors disabled:cursor-not-allowed"
                          style={isActive
                            ? { background: 'rgba(52,211,153,.15)', color: '#15803d', border: '1px solid rgba(52,211,153,.35)' }
                            : { background: 'transparent', color: '#475569', border: '1px solid #e5e7eb' }
                          }
                        >
                          {isActive ? <><Check size={9} /> Active</> : 'Use'}
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {!lockedByBuilder && (
              <div className="flex items-center gap-1.5 mt-2">
                <input
                  className={inputCls}
                  value={customFlowId}
                  onChange={(e) => setCustomFlowId(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyCustomFlow() } }}
                  placeholder="Custom flow ID — the real OneSDK flow ID configured on this customer's own FrankieOne account"
                />
                <button
                  onClick={applyCustomFlow}
                  disabled={!customFlowId.trim()}
                  className="px-3 py-1.5 rounded-[7px] text-[11px] font-medium shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: '#f8fafc', border: '1px solid #e5e7eb', color: '#475569' }}
                >
                  Use
                </button>
              </div>
            )}

            {activeFlow ? (
              <div className="flex items-center gap-2 mt-2 px-3 py-2 rounded-[8px]" style={{ background: 'rgba(52,211,153,.06)', border: '1px solid rgba(52,211,153,.2)' }}>
                <Zap size={12} className="text-emerald-700 shrink-0" />
                <span className="text-[11px] text-emerald-700">
                  Live demo will use <span className="font-semibold">{activeFlow.name}</span> (<span className="font-mono">{activeFlow.flowId}</span>{activeFlow.provider ? ` · ${activeFlow.provider}` : ''})
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2 mt-2 px-3 py-2 rounded-[8px]" style={{ background: 'rgba(52,211,153,.06)', border: '1px solid rgba(52,211,153,.2)' }}>
                <Zap size={12} className="text-emerald-700 shrink-0" />
                <span className="text-[11px] text-emerald-700">
                  Live demo will use <span className="font-semibold">Custom flow</span> (<span className="font-mono">{activeFlowId}</span>)
                </span>
              </div>
            )}
          </div>

          <SubSection title="SMS & Send Link" description="SMS delivery settings for sending verification links to applicants">
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-[9px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-[#1e293b]">Send SMS</p>
                <p className="text-[10px] text-[#64748b] mt-0.5">Texts the verification link to the applicant instead of (or as well as) showing it in-app.</p>
              </div>
              <Toggle checked={smsConfig.sendSms} onChange={() => setSmsConfig((c) => ({ ...c, sendSms: !c.sendSms }))} />
            </div>
            {smsConfig.sendSms && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className={labelCls}>SMS Sender ID</label>
                    <input className={inputCls} value={smsConfig.senderId} onChange={(e) => setSmsConfig((c) => ({ ...c, senderId: e.target.value }))} placeholder="Letters, digits, spaces (max 11 chars)" />
                  </div>
                  <div>
                    <label className={labelCls}>Session Expiry (hours)</label>
                    <input type="number" min={1} className={inputCls} value={smsConfig.sessionExpiryHours} onChange={(e) => setSmsConfig((c) => ({ ...c, sessionExpiryHours: Number(e.target.value) || 1 }))} />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Verification Domain</label>
                  <input className={inputCls} value={smsConfig.verificationDomain} onChange={(e) => setSmsConfig((c) => ({ ...c, verificationDomain: e.target.value }))} />
                </div>
                <div>
                  <label className={labelCls}>SMS Message Template</label>
                  <textarea
                    className={inputCls}
                    rows={3}
                    value={smsConfig.messageTemplate}
                    onChange={(e) => setSmsConfig((c) => ({ ...c, messageTemplate: e.target.value }))}
                  />
                  <p className="text-[10px] text-[#475569] mt-1">Tokens: <span className="font-mono">{'{applicantName}'}</span>, <span className="font-mono">{'{verificationLink}'}</span>, <span className="font-mono">{'{sessionExpiryHours}'}</span></p>
                </div>
              </>
            )}
          </SubSection>

          <SubSection title="Vendor Settings" description="Connector configuration for verification vendors — process definitions for IDV, OCR and biometric verification">
            {lockedByBuilder ? (
              <BuilderLockedBanner>
                Confirming the <span className="font-semibold">{defaultProvider}</span> connector already set up for this journey — change it in Workflow Builder if a different vendor is needed.
              </BuilderLockedBanner>
            ) : (
            <p className="text-[10px] text-[#475569]">
              This controls which connector FrankieOne&apos;s IDV service routes to by default. A flow above can still name a specific vendor and override this.
            </p>
            )}
            <div className="flex gap-1.5 flex-wrap">
              {IDV_PROVIDERS.map((p) => (
                <button
                  key={p}
                  onClick={() => setDefaultProvider(p)}
                  disabled={lockedByBuilder}
                  className="px-2.5 py-1 rounded-[7px] text-[11px] font-medium transition-colors disabled:cursor-not-allowed"
                  style={defaultProvider === p
                    ? { background: 'rgba(99,102,241,.18)', border: '1px solid rgba(99,102,241,.45)', color: '#4f46e5' }
                    : { background: '#f8fafc', border: '1px solid #e5e7eb', color: '#475569', opacity: lockedByBuilder ? 0.5 : 1 }
                  }
                >
                  {p}
                </button>
              ))}
            </div>

            <div className="space-y-2">
              {IDV_PROVIDERS.map((provider) => {
                const settings = vendorSettings[provider]
                return (
                  <SubSection
                    key={provider}
                    title={provider}
                    description={provider === defaultProvider ? 'Default connector — process definitions for IDV, OCR and biometric verification' : 'Process definitions for IDV, OCR and biometric verification'}
                    defaultOpen={provider === defaultProvider}
                  >
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-[9px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-semibold text-[#1e293b]">Mock Mode</p>
                        <p className="text-[10px] text-[#64748b] mt-0.5">
                          {settings.mockMode
                            ? `Mock mode is on. ${provider} connector returns simulated results instead of calling the real vendor.`
                            : `Mock mode is off. ${provider} connector communicates with the real ${provider} service.`}
                        </p>
                      </div>
                      <Toggle checked={settings.mockMode} onChange={() => updateVendor(provider, { mockMode: !settings.mockMode })} disabled={lockedByBuilder} />
                    </div>
                    <ProcessConfigFields label="IDV Process" value={settings.idvProcess} onChange={(v) => updateVendor(provider, { idvProcess: v })} disabled={lockedByBuilder} />
                    <ProcessConfigFields label="OCR Process" value={settings.ocrProcess} onChange={(v) => updateVendor(provider, { ocrProcess: v })} disabled={lockedByBuilder} />
                    <ProcessConfigFields label="Biometrics Process" value={settings.biometricsProcess} onChange={(v) => updateVendor(provider, { biometricsProcess: v })} disabled={lockedByBuilder} />
                  </SubSection>
                )
              })}
            </div>
          </SubSection>

          {/* Accepted Documents & Countries */}
          <div>
            <label className={labelCls}>Accepted Documents &amp; Countries</label>
            <p className="text-[10px] text-[#475569] mb-2">Per-country overrides for which document types are accepted. Countries without an override fall through to the platform default (unrestricted).</p>
            <div className="space-y-2">
              {restrictions.map((r) => (
                <div key={r.countryCode} className="rounded-[9px] p-3" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-semibold text-[#1e293b]">{r.countryLabel}</span>
                    <button onClick={() => removeCountry(r.countryCode)} className="text-[10px] text-red-700/70 hover:text-red-700 transition-colors">Remove</button>
                  </div>
                  <label className="flex items-center gap-2 text-[11px] text-[#475569] mb-2 cursor-pointer">
                    <input type="checkbox" checked={r.restrict} onChange={() => toggleRestrict(r.countryCode)} style={{ accentColor: '#6366f1' }} />
                    Restrict to selected document types
                  </label>
                  {r.restrict && (
                    <div className="flex flex-wrap gap-3 pl-1">
                      {DOC_TYPES.map((d) => (
                        <label key={d} className="flex items-center gap-1.5 text-[11px] text-[#475569] cursor-pointer">
                          <input type="checkbox" checked={r.docTypes.includes(d)} onChange={() => toggleDocType(r.countryCode, d)} style={{ accentColor: '#6366f1' }} />
                          {d}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="relative mt-2">
              <button
                onClick={() => setAddCountryOpen((o) => !o)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] rounded-[8px] text-[#64748b] hover:text-blue-700 border border-dashed border-[#e5e7eb] hover:border-blue-600 transition-colors"
              >
                <Plus size={12} /> Add country
              </button>
              {addCountryOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setAddCountryOpen(false)} />
                  <div className="absolute top-full left-0 mt-1 w-40 rounded-[8px] overflow-hidden z-50" style={{ background: '#ffffff', border: '1px solid #e5e7eb' }}>
                    {availableCountries.length === 0 ? (
                      <p className="px-3 py-2 text-[10px] text-[#475569] italic">All countries added</p>
                    ) : availableCountries.map((c) => (
                      <button
                        key={c.code}
                        onClick={() => addCountry(c.code, c.label)}
                        className="w-full text-left px-3 py-1.5 text-[11px] text-[#475569] hover:bg-[#ffffff] hover:text-[#1e293b] transition-colors"
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <SubSection title="Telemetry & Audit Events" description="Configure which SDK events are forwarded as audit log entries">
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-[9px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-[#1e293b]">Forward events to audit log</p>
                <p className="text-[10px] text-[#64748b] mt-0.5">Sends matching OneSDK events (document capture, errors, submissions) to this customer&apos;s audit log.</p>
              </div>
              <Toggle checked={forwardAuditEvents} onChange={() => setForwardAuditEvents((v) => !v)} />
            </div>
            {forwardAuditEvents && (
              <div>
                <label className={labelCls}>Allowed events</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {auditEvents.map((pattern) => (
                    <span key={pattern} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono" style={{ background: '#f8fafc', border: '1px solid #e5e7eb', color: '#475569' }}>
                      {pattern}
                      <button onClick={() => removeAuditEvent(pattern)} className="hover:text-red-700"><X size={9} /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-1.5">
                  <input
                    className={inputCls}
                    placeholder="e.g. *:ERROR"
                    value={newAuditEvent}
                    onChange={(e) => setNewAuditEvent(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAuditEvent() } }}
                  />
                  <button onClick={addAuditEvent} className="px-3 py-1.5 rounded-[7px] text-[11px] font-medium shrink-0" style={{ background: '#f8fafc', border: '1px solid #e5e7eb', color: '#475569' }}>
                    Add
                  </button>
                </div>
                <p className="text-[10px] text-[#475569] mt-1 flex items-center gap-1.5"><Info size={10} className="shrink-0" /> Supports wildcard patterns using * (e.g. INIT, *:ERROR, SUBMIT*)</p>
              </div>
            )}
          </SubSection>
        </div>
      )}
    </div>
  )
}
